import { driver, type DriveStep, type PopoverDOM } from "driver.js";
import { useEffect, useRef, type FC } from "react";
import "driver.js/dist/driver.css";
import "./CallCenterTour.driver.scss";
import {
  tourAnchorByKey,
  type TourFocus,
  type TourShellTab,
  type TourSide,
} from "@shared/helpers/call-center-tour";
import { CC } from "./call-center-ui.theme";

export type { TourFocus, TourShellTab };

export type TourPreparePayload = {
  shellTab: TourShellTab;
  focus: TourFocus;
};

export type SavedTourStep = {
  anchorKey: string;
  kicker: string;
  title: string;
  description: string;
  side: string;
};

type CatalogStep = {
  element: string | null;
  shellTab: TourShellTab;
  focus: TourFocus;
  kicker: string;
  title: string;
  description: string;
  side: TourSide;
};

const SIDES: TourSide[] = ["top", "right", "bottom", "left"];

function toCatalog(steps: SavedTourStep[]): CatalogStep[] {
  return steps.flatMap((step) => {
    const anchor = tourAnchorByKey(step.anchorKey);
    if (!anchor) return [];
    const side = SIDES.includes(step.side as TourSide)
      ? (step.side as TourSide)
      : "bottom";
    return [
      {
        element: anchor.element,
        shellTab: anchor.shellTab,
        focus: anchor.focus,
        kicker: step.kicker,
        title: step.title,
        description: step.description,
        side,
      },
    ];
  });
}

const KICKER_CLASS = "cc-driver-kicker";

function paintKicker(popover: PopoverDOM, kicker: string) {
  popover.wrapper.querySelector(`.${KICKER_CLASS}`)?.remove();
  const label = document.createElement("div");
  label.className = KICKER_CLASS;
  label.textContent = kicker;
  popover.title.insertAdjacentElement("beforebegin", label);
}

function toDriveSteps(steps: CatalogStep[]): DriveStep[] {
  return steps.map((step) => ({
    ...(step.element ? { element: step.element } : {}),
    disableActiveInteraction: true,
    popover: {
      title: step.title,
      description: step.description,
      side: step.side,
      align: "start" as const,
    },
  }));
}

function waitForSelector(selector: string | null, timeoutMs = 8000): Promise<void> {
  if (!selector) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    };
    if (document.querySelector(selector)) {
      done();
      return;
    }
    const started = Date.now();
    const timer = window.setInterval(() => {
      if (document.querySelector(selector) || Date.now() - started > timeoutMs) {
        window.clearInterval(timer);
        done();
      }
    }, 40);
  });
}

export const CallCenterTour: FC<{
  open: boolean;
  steps: SavedTourStep[];
  onClose: () => void;
  onPrepare: (payload: TourPreparePayload) => void;
}> = ({ open, steps, onClose, onPrepare }) => {
  const onPrepareRef = useRef(onPrepare);
  const onCloseRef = useRef(onClose);
  onPrepareRef.current = onPrepare;
  onCloseRef.current = onClose;
  const movingRef = useRef(false);

  useEffect(() => {
    if (!open || steps.length === 0) return;
    const catalog = toCatalog(steps);
    if (catalog.length === 0) return;
    movingRef.current = false;

    const prepare = (step: CatalogStep | undefined) => {
      if (!step) return Promise.resolve();
      onPrepareRef.current({ shellTab: step.shellTab, focus: step.focus });
      return waitForSelector(step.element);
    };

    let cleanup = () => {};
    let cancelled = false;
    const start = window.setTimeout(() => {
      void prepare(catalog[0]).then(() => {
        if (cancelled) return;
        const instance = driver({
          steps: toDriveSteps(catalog),
          overlayColor: CC.ink,
          overlayOpacity: 0.55,
          stagePadding: 10,
          stageRadius: CC.radiusSm,
          smoothScroll: true,
          allowClose: true,
          disableActiveInteraction: true,
          showProgress: true,
          progressText: "{{current}} מתוך {{total}}",
          nextBtnText: "הבא",
          prevBtnText: "הקודם",
          doneBtnText: "סיום",
          popoverClass: "cc-driver-popover",
          onPopoverRender: (popover, { state }) => {
            const step = catalog[state.activeIndex ?? 0];
            if (step) paintKicker(popover, step.kicker);
          },
          onNextClick: (_element, _step, { driver: d, state }) => {
            if (movingRef.current) return;
            const nextIdx = (state.activeIndex ?? 0) + 1;
            const next = catalog[nextIdx];
            if (!next) {
              d.destroy();
              onCloseRef.current();
              return;
            }
            movingRef.current = true;
            void prepare(next).then(() => {
              movingRef.current = false;
              d.moveNext();
            });
          },
          onPrevClick: (_element, _step, { driver: d, state }) => {
            if (movingRef.current) return;
            const prevIdx = (state.activeIndex ?? 0) - 1;
            const prev = catalog[prevIdx];
            if (!prev) return;
            movingRef.current = true;
            void prepare(prev).then(() => {
              movingRef.current = false;
              d.movePrevious();
            });
          },
          onDestroyStarted: () => {
            if (!instance.isActive()) return;
            instance.destroy();
            onCloseRef.current();
          },
        });

        instance.drive();
        cleanup = () => {
          if (instance.isActive()) instance.destroy();
        };
      });
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(start);
      cleanup();
    };
  }, [open, steps]);

  return null;
};
